/**
 * Builder do PDF do DRE Gerencial REAL (Task #186 — consome /api/v2/dre).
 *
 * Substitui o antigo caminho mock (`dreMesAtual()` em mockData) que o TopBar
 * usava: o export agora reflete EXATAMENTE o que está na tela — mesmo regime
 * (Competência/Caixa), mesmo mês, mesma resposta v2 renderizada pela
 * `DreGerencialTable`. Reusa `DISPLAY_ORDER` + `displayValor` da tabela como
 * fonte única de ordenação/sinal, então tela e PDF nunca divergem.
 *
 * Estrutura (espelha a tela):
 *   - 16 linhas operacionais (R1-R6 + D1-D10), indentadas; R6 = "Em breve".
 *   - 7 calculadas em destaque (Receita Líquida → Lucro Líquido); EBITDA e
 *     Lucro Líquido com realce forte.
 *   - Seção INFORMATIVO (fora-DRE) com D11 (mútuos), abaixo do Lucro Líquido.
 *   - Itens ausentes na resposta (EBIT/Receita Líquida Ajustada no regime
 *     caixa) são pulados, igual à tela.
 */
import type { DreV2Response, DreLinhaV2, DreCalculadaV2, DreRegime } from "../api/types";
import { fmtBRL } from "./formatters";
import {
  DISPLAY_ORDER,
  CALC_DESTAQUE_FORTE,
  displayValor,
} from "../components/financeiro/DreGerencialTable";
import type { PdfInput, PdfRowStyle } from "./pdfExport";

const MESES = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

/** "2026-05" → "Mai/2026". Fallback pro próprio input se mal-formado. */
export function mesRefLabel(mesRef: string): string {
  const [ano, mes] = mesRef.split("-");
  const idx = Number(mes) - 1;
  if (!ano || Number.isNaN(idx) || idx < 0 || idx > 11) return mesRef;
  return `${MESES[idx]}/${ano}`;
}

function regimeLabel(regime: DreRegime): string {
  return regime === "competencia" ? "Competência" : "Caixa";
}

const INDENT = "    "; // recuo hierárquico das linhas operacionais/informativas.

export function buildDrePdfInput(
  data: DreV2Response,
  regime: DreRegime,
  mesRef: string,
): PdfInput {
  const linhasPorCodigo = new Map<string, DreLinhaV2>();
  for (const secao of data.secoes) {
    for (const l of secao.linhas) linhasPorCodigo.set(l.codigo, l);
  }
  const calcPorCodigo = new Map<string, DreCalculadaV2>();
  for (const c of data.calculadas) calcPorCodigo.set(c.codigo, c);

  const body: Array<Array<string>> = [];
  const rowStyles: Array<PdfRowStyle | undefined> = [];
  let linhasDre = 0; // operacionais + calculadas + informativas (sem cabeçalho de seção)

  for (const item of DISPLAY_ORDER) {
    if (item.kind === "calc") {
      const c = calcPorCodigo.get(item.codigo);
      if (!c) continue; // ex.: EBIT/RECEITA_LIQUIDA_AJUSTADA ausentes no caixa.
      body.push([c.nome, fmtBRL(c.valor_atual, { decimals: 0 })]);
      rowStyles.push(CALC_DESTAQUE_FORTE.has(c.codigo) ? "strong" : "highlight");
      linhasDre += 1;
      continue;
    }
    const l = linhasPorCodigo.get(item.codigo);
    if (!l) continue;
    if (l.codigo === "R6") {
      // Fonte #121f ainda não existe — espelha o "Em breve" da tela.
      body.push([`${INDENT}${l.nome}`, "Em breve"]);
      rowStyles.push("muted");
      linhasDre += 1;
      continue;
    }
    body.push([`${INDENT}${l.nome}`, fmtBRL(displayValor(l), { decimals: 0 })]);
    rowStyles.push(undefined);
    linhasDre += 1;
  }

  const informativo = data.secoes.find((s) => s.nome.startsWith("INFORMATIVO"));
  if (informativo && informativo.linhas.length > 0) {
    body.push(["INFORMATIVO (fora-DRE)", ""]);
    rowStyles.push("section");
    for (const l of informativo.linhas) {
      body.push([`${INDENT}${l.nome}`, fmtBRL(displayValor(l), { decimals: 0 })]);
      rowStyles.push("muted");
      linhasDre += 1;
    }
  }

  const cobertura = data.header.cobertura_classificacao_pct.toFixed(1);

  return {
    filename: `cortex-dre-gerencial-${mesRef}-${regime}`,
    title: "DRE Gerencial — Indra Energia",
    subtitle: "Demonstração escalonada — Receita → EBITDA → Lucro Líquido",
    filtros: [`Período: ${mesRefLabel(mesRef)}`, `Regime: ${regimeLabel(regime)}`],
    footerNote: `Cobertura de classificação ${cobertura}% · ${linhasDre} linhas`,
    tables: [
      {
        title: "Demonstração de Resultado",
        head: ["Conta", "Valor (R$)"],
        body,
        rowStyles,
      },
    ],
  };
}
